import pushRoute from '@app/utils/routing';
import {
  Box,
  Icons,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { useDispatch, useSelector } from 'react-redux';
import { LtiProvider } from '@ublend-npm/aula-schema';
import React, { MouseEvent, useState } from 'react';
import DeleteAssignmentModal from './DeleteAssignmentModal';
import exportSubmissionGrades from '@app/actions/assignments/exportGrades.action';
import {
  MANUAL,
  HANDIN_PROVIDER,
  LTI1P3_PROVIDER,
  TURNITIN_PROVIDER,
  QUIZZES_PROVIDER,
} from '@core/assignments/constants';
import { ResponsiveAssignment } from '@core/assignments/types';
import formatDate from '@core/utils/formatDate';
import {
  getAssignmentProvider,
  getIsCoupledProvider,
  isAssignmentProviderDisabled,
} from '@core/assignments/utils';
import { accessingCurrentSpaceAsStaff } from '@core/selectors/classroom';
import { operations as assignmentOperations } from '../../../../../store/assignments';
import { filterAssignmentParams } from '../utils/utils';
import { createStyles } from './AssignmentCard.styles';
import AnonymousIcon from './AnonymousIcon';
import Logo from './Logo';
import { getStatus, getStatusLabel, STATUS } from './utils';

type Props = {
  assignment: ResponsiveAssignment;
  spaceId: string;
  providers: Array<LtiProvider>;
  isEducator?: boolean;
  onMaterialClick?: (materialId: string, materialTitle?: string) => void;
};

type ExportGradesAction = (params: {
  assignment: ResponsiveAssignment & { objectId: string };
  spaceId: string;
}) => ReturnType<typeof exportSubmissionGrades>;

const typedExportSubmissionGrades =
  exportSubmissionGrades as unknown as ExportGradesAction;

const AssignmentCard = ({
  assignment,
  spaceId,
  providers,
  isEducator = false,
  onMaterialClick,
}: Props) => {
  const theme = useTheme();
  const isMdBelow = useMediaQuery(theme.breakpoints.down('md'));
  const styles = createStyles(theme);
  const assignmentId = assignment.id;
  const assignmentRoute = `/dashboard/${spaceId}/journey/assignments/${assignmentId}`;
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const dispatch = useDispatch();
  const accessingAsStaff = useSelector(accessingCurrentSpaceAsStaff);

  const openMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };
  const closeMenu = () => setMenuAnchorEl(null);

  const isHandin = assignment.provider === HANDIN_PROVIDER;
  const isLti1p3 = assignment.provider === LTI1P3_PROVIDER;
  const isManual = assignment.provider === MANUAL;
  const providerItem = getAssignmentProvider(assignment, providers);
  const isCoupled = providerItem
    ? getIsCoupledProvider(providerItem as any)
    : false;
  const isProviderDisabled = isAssignmentProviderDisabled(
    assignment,
    providers
  );
  const providerName = isLti1p3
    ? providerItem?.description
    : {
        'hand-in': 'Handin',
        handin: 'Handin',
        turnitin: 'Turnitin',
        quizzes: 'Quizzes',
        'lti-1p3': 'Lti-1p3',
      }[assignment.provider];
  const linkedMaterialId = assignment.linkedMaterials?.[0]?.id;

  const linkedMaterials = assignment.linkedMaterials || [];
  const firstMaterial = linkedMaterials[0];
  const additionalMaterialsCount = linkedMaterials.length - 1;

  const handleMaterialClick = (
    event: React.MouseEvent | React.KeyboardEvent
  ) => {
    event.stopPropagation();
    if (linkedMaterialId) {
      onMaterialClick?.(
        linkedMaterialId,
        assignment.linkedMaterials?.[0]?.title
      );
    }
  };

  const handleMaterialKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return null;
    }
    event.preventDefault();
    if (linkedMaterialId) {
      onMaterialClick?.(
        linkedMaterialId,
        assignment.linkedMaterials?.[0]?.title
      );
    }
  };

  const onClickAssignment = () => {
    pushRoute(assignmentRoute);
  };

  const assignmentStatus =
    assignment.provider === MANUAL
      ? STATUS.manualAssignment
      : getStatus({
          startDate: assignment.startDate,
          endDate: assignment.endDate,
          status: assignment.status,
          shouldCloseOnDueDate: assignment.shouldCloseOnDueDate,
        });

  const isClosed = assignmentStatus === STATUS.closed;
  const isDark = theme.palette.mode === 'dark';

  const showOpenMenuItem =
    [LTI1P3_PROVIDER, HANDIN_PROVIDER].includes(assignment.provider) &&
    isEducator &&
    !isProviderDisabled;
  const showEditMenuItem =
    [TURNITIN_PROVIDER, QUIZZES_PROVIDER].includes(assignment.provider) &&
    isEducator &&
    !isProviderDisabled &&
    !isCoupled;
  const showEditAssignmentMenuItem =
    isEducator && !isProviderDisabled && isCoupled;
  const isEditEnabledForQuizzes = providerName !== 'Quizzes';

  const menuItems = [
    isEducator &&
      assignment.isHidden && {
        id: 'show-assignment',
        label: 'Show assignment',
        icon: <Icons.VisibilityOutlined fontSize="small" />,
        danger: false,
      },
    isEducator &&
      !assignment.isHidden && {
        id: 'hide-assignment',
        label: 'Hide assignment',
        icon: <Icons.VisibilityOffOutlined fontSize="small" />,
        danger: false,
      },
    showOpenMenuItem && {
      id: 'open-in-provider',
      label: `Open in ${providerName}`,
      icon: <Icons.OpenInNew fontSize="small" />,
      danger: false,
    },
    showEditMenuItem &&
      isEditEnabledForQuizzes && {
        id: 'edit-in-provider',
        label: 'Edit assignment',
        icon: <Icons.EditOutlined fontSize="small" />,
        danger: false,
      },
    showEditAssignmentMenuItem && {
      id: 'edit-assignment',
      label: 'Edit assignment',
      icon: <Icons.EditOutlined fontSize="small" />,
      danger: false,
    },
    (isEducator || accessingAsStaff) && {
      id: 'export-grades',
      label: 'Export grades',
      icon: <Icons.ImportExportOutlined fontSize="small" />,
      onClick: () =>
        dispatch(
          typedExportSubmissionGrades({
            assignment: {
              ...assignment,
              objectId: assignment.objectId || assignment.id,
            },
            spaceId: assignment.classRoom?.objectId || spaceId,
          })
        ),
      danger: false,
    },
    isEducator &&
      isHandin &&
      (isClosed
        ? {
            id: 're-open',
            label: 'Re-open',
            icon: <Icons.LockOpenOutlined fontSize="small" />,
            danger: false,
            onClick: () =>
              dispatch(
                assignmentOperations.reopenAssignment({
                  spaceId: assignment.classRoom?.objectId || spaceId,
                  assignment,
                })
              ),
          }
        : {
            id: 'close',
            label: 'Close',
            icon: <Icons.LockOutlined fontSize="small" />,
            danger: false,
            onClick: () =>
              dispatch(
                assignmentOperations.closeAssignment({
                  spaceId: assignment.classRoom?.objectId || spaceId,
                  assignment,
                })
              ),
          }),
    isEducator &&
      isHandin &&
      !assignment.extensions?.length &&
      (assignment.isAnonymised
        ? {
            id: 'reveal-students',
            label: 'Reveal students',
            icon: <Icons.PeopleOutlined fontSize="small" />,
            danger: false,
            onClick: () =>
              dispatch(
                assignmentOperations.editAssignment({
                  spaceId,
                  assignment: {
                    ...filterAssignmentParams(assignment),
                    isAnonymised: false,
                  },
                  accessToken: undefined,
                })
              ),
          }
        : {
            id: 'anonymise-students',
            label: 'Anonymise students',
            icon: <Icons.PeopleOutlined fontSize="small" />,
            danger: false,
            onClick: () =>
              dispatch(
                assignmentOperations.editAssignment({
                  spaceId,
                  assignment: {
                    ...filterAssignmentParams(assignment),
                    isAnonymised: true,
                  },
                  accessToken: undefined,
                })
              ),
          }),
    isEducator &&
      (!isLti1p3 || isCoupled) && {
        id: 'delete',
        label: 'Delete',
        icon: <Icons.DeleteOutlined fontSize="small" />,
        danger: true,
        onClick: () => setDeleteModalOpen(true),
      },
  ].filter(Boolean);

  let statusStyle = styles.status;

  if (assignmentStatus === STATUS.pastDueDate) {
    statusStyle = styles.pastDueDate;
  } else if (assignmentStatus === STATUS.manualAssignment) {
    statusStyle = styles.manualStatus;
  }

  return (
    <Box onClick={onClickAssignment} sx={styles.cardWrapper}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.cardDetails}>
          <Logo assignment={assignment} providers={providers} />
          {assignmentStatus && (
            <Box sx={statusStyle}>{getStatusLabel(assignmentStatus)}</Box>
          )}
          {assignment.isSubmitted && (
            <Box sx={styles.submittedStatus}>Submitted</Box>
          )}
          {!!assignment.groupSet && (
            <Tooltip
              title="This is a group assignment"
              disableFocusListener={isMdBelow}
              disableHoverListener={isMdBelow}
              disableTouchListener={isMdBelow}
            >
              <Box sx={styles.headerIcon}>
                <Icons.GroupOutlined />
              </Box>
            </Tooltip>
          )}
          {assignment.isAnonymised && (
            <Tooltip
              title="This assignment is anonymised"
              disableFocusListener={isMdBelow}
              disableHoverListener={isMdBelow}
              disableTouchListener={isMdBelow}
            >
              <Box sx={styles.headerIcon}>
                <AnonymousIcon />
              </Box>
            </Tooltip>
          )}
          {assignment.isHidden && (
            <Tooltip
              title="This assignment is hidden from students"
              disableFocusListener={isMdBelow}
              disableHoverListener={isMdBelow}
              disableTouchListener={isMdBelow}
            >
              <Box sx={styles.headerIcon}>
                <Icons.VisibilityOffOutlined />
              </Box>
            </Tooltip>
          )}
        </Box>
        {!isManual && (isEducator || accessingAsStaff) && (
          <Box sx={styles.menuIcon}>
            <Tooltip
              title="Assignment options"
              disableFocusListener={isMdBelow}
              disableHoverListener={isMdBelow}
              disableTouchListener={isMdBelow}
            >
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <IconButton
                  id="assignment-options"
                  type="button"
                  aria-label="Assignment options"
                  onClick={openMenu}
                >
                  <Icons.MoreVert fontSize="small" />
                </IconButton>
              </Box>
            </Tooltip>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={closeMenu}
              aria-labelledby="assignment-options"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                style: {
                  backgroundColor: isDark ? '#363636' : undefined,
                  color: isDark ? '#ffffff' : undefined,
                  width: '240px',
                  marginTop: '4px',
                },
              }}
              sx={{
                '& .MuiPaper-root': {
                  backgroundColor: isDark ? '#363636 !important' : undefined,
                  color: isDark ? '#ffffff' : undefined,
                  width: '240px',
                  marginTop: '4px',
                },
                '& .MuiMenu-paper': {
                  backgroundColor: isDark ? '#363636 !important' : undefined,
                },
                '& .MuiPopover-paper': {
                  backgroundColor: isDark ? '#363636 !important' : undefined,
                },
                '& .MuiMenu-list': {
                  backgroundColor: isDark ? '#363636 !important' : undefined,
                },
                '& .MuiList-root': {
                  backgroundColor: isDark ? '#363636 !important' : undefined,
                },
                '& .MuiListItemText-primary': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            >
              {menuItems.map((item: any) => (
                <MenuItem
                  key={item.id}
                  icon={item.icon}
                  label={item.label}
                  data-testid={`assignment-${item.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeMenu();
                    item.onClick?.();
                  }}
                />
              ))}
            </Menu>
          </Box>
        )}
      </Box>
      <Box sx={styles.title}>{assignment.title}</Box>
      {assignment.extensions && (
        <Box sx={styles.extensions}>
          <Icons.ExtensionOutlined sx={styles.extensionsIcon} />
          <span>{assignment.extensions.length} extensions available</span>
        </Box>
      )}
      {firstMaterial && (
        <Box
          sx={styles.materials}
          onClick={handleMaterialClick}
          role="button"
          tabIndex={0}
          onKeyDown={handleMaterialKeyDown}
        >
          <Icons.BookOutlined sx={styles.materialsIcon} />
          <Box sx={styles.materialsTitle}>{firstMaterial.title}</Box>
          {additionalMaterialsCount > 0 && (
            <Box sx={styles.materialsCount}>+ {additionalMaterialsCount}</Box>
          )}
        </Box>
      )}
      {assignment.endDate && (
        <Box sx={styles.endDate}>Due: {formatDate(assignment.endDate)}</Box>
      )}

      <DeleteAssignmentModal
        open={deleteModalOpen}
        assignmentTitle={assignment.title}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => {
          // TODO: implement delete functionality
          setDeleteModalOpen(false);
        }}
      />
    </Box>
  );
};

export default AssignmentCard;
